import React, { useState, useEffect } from 'react';
import { TrendingUp, Plus, Minus, ShieldCheck, Mail, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEP_RATE = 0.15;   // ~15%/yr declining-balance depreciation
const FLOOR = 0.10;      // never value the old machine below 10% of its purchase price
const MONTHS = 36;       // 3-year payback period
const DEFAULT_MARGIN = 25;   // % profit margin (admin-tunable)
const DEFAULT_CONTRIB = 10;  // % of profit contributed toward the rebuild (admin-tunable)

const money = (n) => `$${Math.round(n).toLocaleString()}`;

const Row = ({ label, value, setValue, step, prefix = '$', suffix = '', min = 0, testid }) => {
  const clamp = (n) => Math.max(min, Math.round(n));
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <label className="text-sm font-medium text-[#003456]">{label}</label>
      <div className="flex items-center gap-1.5">
        <button type="button" onClick={() => setValue(clamp(value - step))} className="h-7 w-7 rounded-md border border-slate-300 text-[#003456] hover:bg-slate-100 flex items-center justify-center" data-testid={`${testid}-minus`}><Minus className="h-4 w-4" /></button>
        <div className="flex items-center rounded-md border border-slate-300 px-2">
          {prefix && <span className="text-sm text-[#B87333] font-bold">{prefix}</span>}
          <input type="number" value={value} min={min} step={step} onChange={(e) => setValue(clamp(parseFloat(e.target.value) || 0))} className="w-20 text-center text-sm font-bold text-[#B87333] py-1 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`${testid}-input`} />
          {suffix && <span className="text-xs text-slate-500 ml-1">{suffix}</span>}
        </div>
        <button type="button" onClick={() => setValue(clamp(value + step))} className="h-7 w-7 rounded-md border border-slate-300 text-[#003456] hover:bg-slate-100 flex items-center justify-center" data-testid={`${testid}-plus`}><Plus className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export const ROICalculator = () => {
  const [purchase, setPurchase] = useState(5000);
  const [age, setAge] = useState(4);
  const [rebuild, setRebuild] = useState(3750);
  const [replace, setReplace] = useState(9500);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [earns, setEarns] = useState(false);
  const [monthlyRevenue, setMonthlyRevenue] = useState(2000);
  const [owing, setOwing] = useState(0);
  const [marginPct, setMarginPct] = useState(DEFAULT_MARGIN);
  const [contribPct, setContribPct] = useState(DEFAULT_CONTRIB);

  useEffect(() => {
    axios.get(`${API_URL}/api/settings`)
      .then((res) => {
        const m = Number(res.data?.roi_profit_margin_percent);
        const c = Number(res.data?.roi_contribution_percent);
        if (m > 0) setMarginPct(m);
        if (c > 0) setContribPct(c);
      })
      .catch(() => {});
  }, []);

  const PROFIT_MARGIN = marginPct / 100;
  const CONTRIB_RATE = contribPct / 100;

  // What the existing machine is still worth today (purchase price less depreciation for its age).
  const depreciated = purchase * Math.pow(1 - DEP_RATE, age);
  const currentValue = Math.max(depreciated, purchase * FLOOR);

  // Two ways to look at the cost of replacing:
  //  - REAL: actual cash out of pocket = new machine + whatever loan balance is still owed.
  //  - EXPECTED: economic cost incl. the depreciable asset you write off (residual value or loan, whichever is higher).
  const realWriteOff = owing;
  const expectedWriteOff = Math.max(currentValue, owing);
  const realReplaceTotal = replace + realWriteOff;
  const expectedReplaceTotal = replace + expectedWriteOff;

  // Rebuild = added cost to keep a machine you already own. Savings are measured against the REAL (cash) cost.
  const rebuildMonthly = rebuild / MONTHS;
  const realReplaceMonthly = realReplaceTotal / MONTHS;
  const expectedReplaceMonthly = expectedReplaceTotal / MONTHS;
  const monthlySavings = Math.max(0, realReplaceMonthly - rebuildMonthly);
  const totalSavings = Math.max(0, realReplaceTotal - rebuild);

  // Optional: if the machine earns money, how fast the rebuild pays for itself.
  // We use profit (25% margin) and set aside only 10% of that profit toward the rebuild,
  // so payback reflects a realistic contribution, not every dollar of profit.
  const monthlyProfit = earns ? monthlyRevenue * PROFIT_MARGIN : 0;
  const monthlyContribution = monthlyProfit * CONTRIB_RATE;
  const paybackMonths = earns && monthlyContribution > 0 ? rebuild / monthlyContribution : null;
  const replacePaybackMonths = earns && monthlyContribution > 0 ? realReplaceTotal / monthlyContribution : null;
  const fmtPayback = (m) => {
    if (m == null) return '—';
    if (m < 1.5) return `${Math.max(1, Math.round(m * 4.33))} weeks`;
    if (m >= 18) return `${(m / 12).toFixed(1)} years`;
    return `${m.toFixed(1)} months`;
  };

  const emailEstimate = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) { toast.error('Please enter a valid email.'); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/leads/roi-estimate`, {
        email,
        source: typeof window !== 'undefined' ? window.location.pathname : 'roi-calculator',
        current_value: money(currentValue),
        rebuild_total: money(rebuild),
        rebuild_monthly: money(rebuildMonthly),
        replace_total: money(realReplaceTotal),
        replace_monthly: money(realReplaceMonthly),
        new_machine: money(replace),
        written_off: money(realWriteOff),
        expected_total: money(expectedReplaceTotal),
        expected_write_off: money(expectedWriteOff),
        owing: money(owing),
        total_savings: money(totalSavings),
        monthly_savings: money(monthlySavings),
        earns,
        monthly_revenue: money(monthlyRevenue),
        monthly_profit: money(monthlyProfit),
        monthly_contribution: money(monthlyContribution),
        margin_pct: String(marginPct),
        contrib_pct: String(contribPct),
        payback: paybackMonths != null ? fmtPayback(paybackMonths) : '',
        replace_payback: replacePaybackMonths != null ? fmtPayback(replacePaybackMonths) : '',
      });
    } catch (err) { /* non-blocking */ }
    setSending(false);
    setSent(true);
    toast.success('Sent! Check your inbox for your rebuild ROI numbers.');
  };

  return (
    <section id="roi" className="py-20 section-padding bg-white scroll-mt-24" data-testid="roi-calculator">
      <div className="max-w-5xl mx-auto">
        <div className="max-w-2xl mb-8">
          <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Rebuild ROI</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Rebuild the machine you own, or write it off?</h2>
          <p className="text-slate-600 mt-3">
            We start with what your machine is still worth today, then weigh the <strong>added cost of a rebuild</strong> against
            the <strong>full cost of replacing it</strong> (new machine plus the value written off on the old one), over a 3 year payback period.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Inputs */}
          <div className="bg-[#F1F5F9] border border-slate-200 rounded-2xl p-6">
            <Row label="Original purchase price" value={purchase} setValue={setPurchase} step={250} testid="roi-purchase" min={0} />
            <Row label="How old is the machine?" value={age} setValue={setAge} step={1} prefix="" suffix="yrs" testid="roi-age" min={0} />
            <Row label="Rebuild cost (added cost)" value={rebuild} setValue={setRebuild} step={250} testid="roi-rebuild" min={0} />
            <Row label="New machine cost (replacement)" value={replace} setValue={setReplace} step={500} testid="roi-replace" min={0} />
            <Row label="Amount still owing on the machine" value={owing} setValue={setOwing} step={250} testid="roi-owing" min={0} />
            <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
              Estimated value today assumes roughly {Math.round(DEP_RATE * 100)}%/yr depreciation on the original price. If you still owe more than that, we use the loan balance as the write off. These are estimates only; adjust the numbers to match your machine.
            </p>
            <label className="flex items-center gap-2 mt-4 cursor-pointer select-none">
              <input type="checkbox" checked={earns} onChange={(e) => setEarns(e.target.checked)} className="h-4 w-4 accent-[#CE8946]" data-testid="roi-earns-toggle" />
              <span className="text-sm font-medium text-[#003456]">This machine earns money</span>
            </label>
            {earns && (
              <div className="mt-2">
                <Row label="Revenue it makes / month" value={monthlyRevenue} setValue={setMonthlyRevenue} step={250} testid="roi-revenue" min={0} />
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                  We assume a ~{marginPct}% profit margin, and set aside {contribPct}% of that profit toward the rebuild. So {money(monthlyRevenue)}/mo revenue = {money(monthlyProfit)}/mo profit, {money(monthlyContribution)}/mo toward payback.
                </p>
              </div>
            )}
          </div>

          {/* Results */}
          <div className="bg-[#003456] rounded-2xl p-8 text-white">
            <div className="flex items-center gap-2 text-[#CE8946] font-semibold text-sm mb-5"><TrendingUp className="h-5 w-5" /> Over a 3 year payback period</div>

            {/* Current value */}
            <div className="pb-5 border-b border-white/10">
              <p className="text-white/60 text-sm">Your machine's estimated value today</p>
              <p className="text-3xl font-extrabold text-white" data-testid="roi-current-value">~{money(currentValue)}</p>
              <p className="text-white/40 text-xs mt-1">Estimated from {money(purchase)} when new, less depreciation for {age} {age === 1 ? 'year' : 'years'} of age. Actual value varies by condition.</p>
            </div>

            {/* Two streams, amortized over 24 months */}
            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="bg-[#023F63] border border-[#CE8946]/40 rounded-xl p-4">
                <p className="text-[#CE8946] text-xs font-bold uppercase tracking-wide">Rebuild</p>
                <p className="text-2xl font-extrabold text-white mt-1" data-testid="roi-rebuild-monthly">{money(rebuildMonthly)}<span className="text-sm font-medium text-white/50">/mo</span></p>
                <p className="text-white/50 text-xs mt-1">{money(rebuild)} total. You keep the machine you own.</p>
              </div>
              <div className="bg-[#023F63] border border-white/10 rounded-xl p-4">
                <p className="text-white/60 text-xs font-bold uppercase tracking-wide">Replace</p>
                <p className="text-2xl font-extrabold text-white mt-1" data-testid="roi-replace-monthly">{money(realReplaceMonthly)}<span className="text-sm font-medium text-white/50">/mo</span></p>
                <p className="text-white/50 text-xs mt-1">Real cash cost, amortized over 3 years.</p>
              </div>
            </div>

            {/* Real vs Expected replacement */}
            <div className="mt-3 grid grid-cols-2 gap-3" data-testid="roi-real-vs-expected">
              <div className="rounded-xl border border-white/10 p-3">
                <p className="text-white/50 text-[11px] font-bold uppercase tracking-wide">Real replacement</p>
                <p className="text-lg font-extrabold text-white" data-testid="roi-real-replace">{money(realReplaceTotal)}</p>
                <p className="text-white/40 text-[11px] mt-0.5">{money(replace)} new + {money(realWriteOff)} still owed. Actual cash out of pocket.</p>
              </div>
              <div className="rounded-xl border border-white/10 p-3">
                <p className="text-white/50 text-[11px] font-bold uppercase tracking-wide">Expected replacement</p>
                <p className="text-lg font-extrabold text-white/80" data-testid="roi-expected-replace">{money(expectedReplaceTotal)}</p>
                <p className="text-white/40 text-[11px] mt-0.5">{money(replace)} new + {money(expectedWriteOff)} lost machine value written off.</p>
              </div>
            </div>

            {/* Savings */}
            <div className="mt-5 pt-5 border-t border-white/10">
              <p className="text-white/60 text-sm">You keep in your pocket by rebuilding</p>
              <p className="text-4xl font-extrabold text-[#CE8946]" data-testid="roi-savings">{money(totalSavings)}</p>
              <p className="text-white/50 text-sm mt-1">≈ <span className="text-white font-semibold" data-testid="roi-monthly-savings">{money(monthlySavings)}/mo</span> less over a 3 year payback</p>
            </div>

            {earns && monthlyRevenue > 0 && (
              <div className="mt-5 pt-5 border-t border-white/10" data-testid="roi-payback-block">
                <p className="text-white/60 text-sm">Because this machine earns money</p>
                <p className="text-2xl font-extrabold text-white mt-1 leading-snug">
                  Your rebuild pays for itself in <span className="text-[#CE8946]" data-testid="roi-payback">{fmtPayback(paybackMonths)}</span>
                </p>
                <p className="text-white/50 text-sm mt-1">
                  A brand new machine would take <span className="text-white font-semibold" data-testid="roi-replace-payback">{fmtPayback(replacePaybackMonths)}</span> of the same contribution to earn back.
                </p>
                <p className="text-white/40 text-xs mt-2 leading-relaxed" data-testid="roi-payback-math">
                  Based on {money(monthlyRevenue)}/mo revenue &times; {marginPct}% profit margin = {money(monthlyProfit)}/mo profit, with {contribPct}% of that ({money(monthlyContribution)}/mo) set aside toward the rebuild. The rest of your profit stays in your pocket.
                </p>
              </div>
            )}

            {/* Sunk cost + confidence */}
            <div className="mt-5 flex items-start gap-2 bg-[#CE8946]/10 border border-[#CE8946]/30 rounded-xl p-3">
              <ShieldCheck className="h-5 w-5 text-[#CE8946] flex-shrink-0 mt-0.5" />
              <p className="text-white/80 text-xs leading-relaxed" data-testid="roi-confidence">
                Scrapping a machine that has not been rebuilt does not erase its cost. That unit still carries an
                estimated residual value of about <span className="text-[#CE8946] font-semibold">{money(currentValue)}</span>
                {owing > 0 && (<>, and you still owe <span className="text-[#CE8946] font-semibold">{money(owing)}</span> in capital and interest on it. The remaining payments are still owed even after it is hauled away</>)}
                {owing <= 0 && (<>, and if it was financed, the remaining capital and interest payments are still owed even after it is hauled away</>)}.
                Replacing costs <span className="text-[#CE8946] font-semibold">{money(realReplaceTotal)}</span> in real cash (new machine plus any balance still owed), or <span className="text-[#CE8946] font-semibold">{money(expectedReplaceTotal)}</span> once the machine's lost value is counted, on top of keeping none of it. Across a 3 year payback, a rebuilt unit
                with new critical parts, a known service history and our 90 day warranty usually earns more confidence
                than an untested new machine.
              </p>
            </div>

            {/* Email me these numbers */}
            <div className="mt-5 pt-5 border-t border-white/10" data-testid="roi-email-capture">
              {sent ? (
                <p className="inline-flex items-center gap-2 text-sm font-semibold text-white" data-testid="roi-email-sent">
                  <CheckCircle2 className="h-5 w-5 text-[#CE8946]" /> Sent! Your numbers are on the way, we'll follow up about a rebuild.
                </p>
              ) : (
                <>
                  <p className="text-white/70 text-sm mb-2 flex items-center gap-2"><Mail className="h-4 w-4 text-[#CE8946]" /> Email me these numbers</p>
                  <form onSubmit={emailEstimate} className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="flex-1 px-4 py-3 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]"
                      data-testid="roi-email-input"
                    />
                    <button
                      type="submit"
                      disabled={sending}
                      className="inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] disabled:opacity-60 text-[#003456] font-bold px-6 py-3 rounded-md transition-colors whitespace-nowrap"
                      data-testid="roi-email-submit"
                    >
                      {sending ? 'Sending…' : (<>Send my estimate <TrendingUp className="h-4 w-4" /></>)}
                    </button>
                  </form>
                </>
              )}
            </div>

            <p className="text-white/40 text-[11px] mt-4">Estimate only. Final pricing is confirmed in your diagnostic quote; damaged parts and shipping are billed separately.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
